import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';

const EmpoweringInnovation = () => {
  const [activeTab, setActiveTab] = useState(0);

  const tabs = [
    {
      id: 'cybersecurity',
      label: 'Defensive Security',
      title: 'Advanced Protection. Always Active.',
      description: 'Atlas Defenders integrates leading offensive and defensive security strategies, providing teams full visibility into their infrastructure. With 24/7 SOC monitoring and Zero Trust architecture, you get the context you need to stop breaches faster.',
      servicesList: [
        'Endpoint Protection (EDR/XDR)',
        'Network & Perimeter Defense',
        'SIEM & Log Management',
        'Vulnerability Management'
      ],
      cta: 'Learn more',
      ctaLink: '/services/cybersecurity/defensive-security',
      image: '/images/Soc-as-a-service2.png',
      imageClass: 'object-cover object-[center_right]',
      statLabel: 'Threats Blocked',
      statValue: '24/7',
      statColor: 'text-blue-500'
    },
    {
      id: 'infrastructure',
      label: 'Offensive Security',
      title: 'Resilient. Scalable. Secure.',
      description: 'Find your weak spots before attackers do. From deep penetration testing to social engineering and red team engagements.',
      servicesList: [
        'Penetration Testing',
        'Vulnerability Assessment',
        'Social Engineering',
        'Red Team Engagements'
      ],
      cta: 'Learn more',
      ctaLink: '/services/cybersecurity/offensive-security',
      image: '/images/Cyberdefense2.png',
      imageClass: 'object-contain object-right scale-[0.80] origin-right',
      statLabel: 'Uptime',
      statValue: '99.9%',
      statColor: 'text-green-500'
    },
    {
      id: 'software',
      label: 'Network Engineering',
      title: 'Tailored platforms for growth.',
      description: 'Build a robust, secure, and scalable network foundation designed to handle maximum enterprise throughput with ease.',
      servicesList: [
        'Network Design & Architecture',
        'Routing & Switching',
        'Wireless Network Solutions',
        'Network Security Integration'
      ],
      cta: 'Learn more',
      ctaLink: '/services/infrastructure/network-engineering',
      image: '/images/Server administration 6.png',
      imageClass: 'object-contain object-right',
      statLabel: 'Integration',
      statValue: 'Seamless',
      statColor: 'text-purple-500'
    },
    {
      id: 'development',
      label: 'Server Administration',
      title: 'Engineered for the Modern Web.',
      description: 'Comprehensive management of your critical enterprise servers to ensure continuous uptime, security compliance, and performance.',
      servicesList: [
        'Linux & Windows Administration',
        'Server Virtualization',
        'Backup & Disaster Recovery',
        'Performance Optimization'
      ],
      cta: 'Learn more',
      ctaLink: '/services/infrastructure/server-administration',
      image: '/images/Server administration 4.png',
      imageClass: 'object-contain object-right scale-[0.85] translate-x-[5%] origin-right',
      statLabel: 'Performance',
      statValue: '100%',
      statColor: 'text-cyan-500'
    },
    {
      id: 'training',
      label: 'Cloud Integration',
      title: 'Empowering teams with expertise.',
      description: 'Seamlessly migrate and integrate your infrastructure with hybrid or multi-cloud solutions engineered for security and scale.',
      servicesList: [
        'Cloud Migration Services',
        'Multi-Cloud Architecture',
        'Cloud Security Posture Management',
        'DevOps & CI/CD Pipelines'
      ],
      cta: 'Learn more',
      ctaLink: '/services/infrastructure/cloud-integration',
      image: '/images/Cyberdefense3.png',
      imageClass: 'object-cover object-center',
      statLabel: 'Skilled Techs',
      statValue: 'Top Tier',
      statColor: 'text-indigo-500'
    },
    {
      id: 'consulting',
      label: 'Technology Consulting',
      title: 'Strategic vision. Flawless execution.',
      description: 'Aligning top-tier IT strategies with your long-term business goals through deep audits and proactive technical guidance.',
      servicesList: [
        'Strategic IT Planning',
        'Digital Transformation',
        'Cybersecurity Audits',
        'Compliance & Risk Management'
      ],
      cta: 'Learn more',
      ctaLink: '/services/consulting',
      image: '/Logos/Technology consultation.jpg',
      imageClass: 'object-cover object-[center_right]',
      statLabel: 'Compliance',
      statValue: 'Verified',
      statColor: 'text-teal-500'
    },
  ];

  const current = tabs[activeTab];

  return (
    <section className="relative bg-[#020617] overflow-hidden min-h-[800px] flex flex-col justify-center py-20">
      {/* ─── FULL BACKGROUND IMAGE LAYER ─── */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        
        {/* Animated Background Image covering the right side */}
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.div
            key={current.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1, ease: 'easeInOut' }}
            className="absolute right-0 top-0 w-full lg:w-[65%] h-full"
            style={{
              WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 40%, black 100%)',
              maskImage: 'linear-gradient(to right, transparent 0%, black 40%, black 100%)'
            }}
          >
            <img 
              src={current.image} 
              alt={current.title}
              className={`${current.imageClass || 'object-cover object-[center_right]'} w-full h-full opacity-60 mix-blend-screen transition-transform duration-700`}
            />
          </motion.div>
        </AnimatePresence>

        {/* Global Dark gradient backdrop covering entire section to ensure text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#003b7a] via-[#020617]/90 to-transparent" />
      </div>

      <div className="relative z-10 mx-auto w-[90%] xl:w-[80%] max-w-[1800px]">
        
        {/* ─── Ultra Modern Tab Bar ─── */}
        <div className="flex justify-center mb-16 w-full relative z-10 flex-col items-center">
          <div className="flex overflow-x-auto lg:overflow-visible w-full lg:w-auto scrollbar-hide pb-2 lg:pb-0 items-center justify-start lg:justify-center border-b border-white/10">
            <div className="flex items-center space-x-2 md:space-x-8 px-4 w-max mx-auto">
              {tabs.map((tab, index) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(index)}
                  className={`relative px-2 py-4 text-[13px] md:text-[15px] font-medium transition-all duration-300 rounded-none whitespace-nowrap outline-none ${
                    activeTab === index
                      ? 'text-white font-semibold'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <span className="relative z-10">{tab.label}</span>
                  {/* Subtle border underline for active tab */}
                  {activeTab === index && (
                    <motion.div
                      layoutId="modernTabUnderline"
                      className="absolute bottom-0 left-0 right-0 h-[2px] bg-blue-500 rounded-t-sm"
                      transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                    />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ─── Content Area ─── */}
        <div className="relative min-h-[500px]">
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              className="lg:max-w-2xl xl:max-w-3xl pr-4"
            >
              
              {/* Text Content */}
              <div className="space-y-6">
                <h3 className="text-4xl sm:text-5xl lg:text-[54px] font-extrabold text-white leading-[1.1] tracking-tight">
                  {current.title}
                </h3>
                
                <p className="text-blue-100/80 text-lg sm:text-xl leading-relaxed max-w-lg mt-4">
                  {current.description}
                </p>

                {/* Services Checklist */}
                <div className="w-full mt-8 mb-4">
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8">
                    {current.servicesList.map((service, idx) => (
                      <li key={idx} className="flex items-start">
                        <div className="flex-shrink-0 mt-1 flex items-center justify-center w-5 h-5 rounded-full bg-blue-500/20 mr-3">
                          <svg className="w-3 h-3 text-blue-400 font-bold" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className="text-gray-300 hover:text-white transition-colors duration-200 text-[15px] font-medium leading-tight pt-0.5">{service}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-6">
                  <a
                    href={current.ctaLink}
                    className="inline-flex items-center justify-center px-8 py-3.5 text-base font-semibold text-white bg-blue-600 rounded-full hover:bg-blue-500 transition-colors duration-200 shadow-md hover:shadow-lg hover:shadow-blue-500/20"
                  >
                    {current.cta} &rarr;
                  </a>
                </div>
              </div>

            </motion.div>
          </AnimatePresence>
        </div>

      </div>
    </section>
  );
};

export default EmpoweringInnovation;